import { useCallback } from 'react';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useGroupEditorDomainAdapter } from '@/components/group-editor/use-group-editor-domain-adapter';
import type { GroupEditorCommonBindings } from '@/components/group-editor/group-editor-bindings';
import type { ProductPhotoEntity } from '@/entities/product.entity';
import { collectUniqueProductArticles } from '@/shared/product-group-articles';

export type BrandGroupEditorTarget = {
    id: string;
    name: string;
    brandNames: string[];
    brandCount: number;
    productGroupCount: number;
    productCount: number;
};

export type BrandGroupEditorBrand = {
    name: string;
    productGroupCount: number;
    productCount: number;
};

export type BrandGroupEditorData = {
    id: string;
    name: string;
    brands: BrandGroupEditorBrand[];
};

export type UseBrandsGroupEditorArgs = {
    accessToken: string;
    getBrandGroups: (abort?: AbortSignal) => Promise<void>;
    getProducts: (abort?: AbortSignal) => Promise<void>;
    extractApiErrorMessage: (error: unknown) => string | null;
};

type BrandsGroupEditorBindings<TBrandGroup extends BrandGroupEditorTarget> = GroupEditorCommonBindings<
    TBrandGroup,
    BrandGroupEditorData,
    BrandGroupEditorBrand
> & {
    groupEditorPickedBrandNames: string[];
    groupEditorBrandsPage: number;
    setGroupEditorBrandsPage: (page: number) => void;
    groupEditorBrandsOnPage: number;
    onGroupEditorBrandsOnPageUpdate: (count: number) => void;
    groupEditorBrandsTotalRows: number;
    groupEditorBrandsPageCount: number;
    groupEditorPagedBrands: BrandGroupEditorBrand[];
    onUngroupSelectedBrands: () => Promise<void>;
};

export const useBrandsGroupEditor = <TBrandGroup extends BrandGroupEditorTarget>({
    accessToken,
    getBrandGroups,
    getProducts,
    extractApiErrorMessage,
}: UseBrandsGroupEditorArgs): BrandsGroupEditorBindings<TBrandGroup> => {
    const { brandsApi } = useMarketplaceApi();

    const refreshBrandGroupRelatedData = useCallback(async () => {
        await Promise.all([getBrandGroups(), getProducts()]);
    }, [getBrandGroups, getProducts]);

    return useGroupEditorDomainAdapter<
        TBrandGroup,
        BrandGroupEditorData,
        BrandGroupEditorBrand,
        { id: string; name: string },
        { brandNames: string[] },
        { sourceGroupDeleted: boolean },
        BrandsGroupEditorBindings<TBrandGroup>
    >({
        accessToken,
        extractApiErrorMessage,
        adapter: {
            loadGroupEditorDataRequest: async (groupId, options) => {
                const response = await brandsApi.getBrandGroup(groupId, options);
                return response.data;
            },
            refreshAfterMutation: refreshBrandGroupRelatedData,
            getItems: (groupEditorData) => groupEditorData.brands,
            getItemKey: (brand) => brand.name,
            renameGroupRequest: (groupId, payload, options) => brandsApi.renameBrandGroup(groupId, payload, options),
            mapGroupEditorDataAfterRename: (currentData, response) => ({
                ...currentData,
                name: response.name,
            }),
            mapOpenedGroupAfterRename: (currentGroup, response) => ({
                ...currentGroup,
                name: response.name,
            }),
            extractFromGroupRequest: (groupId, payload, options) =>
                brandsApi.extractBrandsFromGroup(groupId, payload, options),
            buildExtractPayload: (brandNames) => ({ brandNames }),
            mapOpenedGroupAfterRefresh: (currentGroup, refreshedGroupData) => ({
                ...currentGroup,
                name: refreshedGroupData.name,
                brandNames: refreshedGroupData.brands.map((brand) => brand.name),
                brandCount: refreshedGroupData.brands.length,
                productGroupCount: refreshedGroupData.brands.reduce(
                    (totalCount, brand) => totalCount + brand.productGroupCount,
                    0,
                ),
                productCount: refreshedGroupData.brands.reduce(
                    (totalCount, brand) => totalCount + brand.productCount,
                    0,
                ),
            }),
            ungroupMessages: {
                emptySelectionWarning: 'Выберите бренды для разгруппировки',
                success: 'Бренды разгруппированы',
                successDeleted: 'Бренды разгруппированы. Исходная группа удалена.',
                error: 'Не удалось разгруппировать бренды',
            },
        },
        mapDomainBindings: ({ groupEditor, commonBindings }) => ({
            ...commonBindings,
            groupEditorPickedBrandNames: groupEditor.pickedValues,
            groupEditorBrandsPage: groupEditor.page,
            setGroupEditorBrandsPage: groupEditor.setPage,
            groupEditorBrandsOnPage: groupEditor.rowsOnPage,
            onGroupEditorBrandsOnPageUpdate: groupEditor.onRowsOnPageUpdate,
            groupEditorBrandsTotalRows: groupEditor.totalRows,
            groupEditorBrandsPageCount: groupEditor.pageCount,
            groupEditorPagedBrands: groupEditor.pagedItems,
            onUngroupSelectedBrands: groupEditor.onUngroupSelected,
        }),
    });
};

export type ProductGroupEditorTarget = {
    id: string;
    name: string;
    brandName?: string | null;
    productCount: number;
    productArticles?: string[];
};

export type ProductGroupEditorProduct = {
    id: string;
    sku: string;
    platform: string;
    name: string;
    brandName: string | null;
    photos: ProductPhotoEntity[] | null;
};

export type ProductGroupEditorData = {
    id: string;
    name: string;
    brandName: string | null;
    products: ProductGroupEditorProduct[];
};

export type UseProductsGroupEditorArgs = {
    accessToken: string;
    getProducts: (abort?: AbortSignal) => Promise<void>;
    extractApiErrorMessage: (error: unknown) => string | null;
};

type ProductsGroupEditorBindings<TProductGroup extends ProductGroupEditorTarget> = GroupEditorCommonBindings<
    TProductGroup,
    ProductGroupEditorData,
    ProductGroupEditorProduct
> & {
    groupEditorPickedProductIds: string[];
    groupEditorProductsPage: number;
    setGroupEditorProductsPage: (page: number) => void;
    groupEditorProductsOnPage: number;
    onGroupEditorProductsOnPageUpdate: (count: number) => void;
    groupEditorProductsTotalRows: number;
    groupEditorProductsPageCount: number;
    groupEditorPagedProducts: ProductGroupEditorProduct[];
    onUngroupSelectedProducts: () => Promise<void>;
};

export const useProductsGroupEditor = <TProductGroup extends ProductGroupEditorTarget>({
    accessToken,
    getProducts,
    extractApiErrorMessage,
}: UseProductsGroupEditorArgs): ProductsGroupEditorBindings<TProductGroup> => {
    const { productsApi } = useMarketplaceApi();
    const refreshProductsGroupRelatedData = useCallback(async () => {
        await getProducts();
    }, [getProducts]);

    return useGroupEditorDomainAdapter<
        TProductGroup,
        ProductGroupEditorData,
        ProductGroupEditorProduct,
        { id: string; name: string; brandName: string | null },
        { productIds: string[] },
        { sourceGroupDeleted: boolean },
        ProductsGroupEditorBindings<TProductGroup>
    >({
        accessToken,
        extractApiErrorMessage,
        adapter: {
            loadGroupEditorDataRequest: async (groupId, options) => {
                const response = await productsApi.getProductGroup(groupId, options);
                return response.data;
            },
            refreshAfterMutation: refreshProductsGroupRelatedData,
            getItems: (groupEditorData) => groupEditorData.products,
            getItemKey: (product) => product.id,
            renameGroupRequest: (groupId, payload, options) =>
                productsApi.renameProductGroup(groupId, payload, options),
            mapGroupEditorDataAfterRename: (currentData, response) => ({
                ...currentData,
                name: response.name,
                brandName: response.brandName,
            }),
            mapOpenedGroupAfterRename: (currentGroup, response) => ({
                ...currentGroup,
                name: response.name,
                brandName: response.brandName,
            }),
            extractFromGroupRequest: (groupId, payload, options) =>
                productsApi.extractProductsFromGroup(groupId, payload, options),
            buildExtractPayload: (productIds) => ({ productIds }),
            mapOpenedGroupAfterRefresh: (currentGroup, refreshedGroupData) => ({
                ...currentGroup,
                name: refreshedGroupData.name,
                brandName: refreshedGroupData.brandName,
                productCount: refreshedGroupData.products.length,
                productArticles: collectUniqueProductArticles(refreshedGroupData.products),
            }),
            ungroupMessages: {
                emptySelectionWarning: 'Выберите товары для разгруппировки',
                success: 'Товары разгруппированы',
                successDeleted: 'Товары разгруппированы. Исходная группа удалена.',
                error: 'Не удалось разгруппировать товары',
            },
        },
        mapDomainBindings: ({ groupEditor, commonBindings }) => ({
            ...commonBindings,
            groupEditorPickedProductIds: groupEditor.pickedValues,
            groupEditorProductsPage: groupEditor.page,
            setGroupEditorProductsPage: groupEditor.setPage,
            groupEditorProductsOnPage: groupEditor.rowsOnPage,
            onGroupEditorProductsOnPageUpdate: groupEditor.onRowsOnPageUpdate,
            groupEditorProductsTotalRows: groupEditor.totalRows,
            groupEditorProductsPageCount: groupEditor.pageCount,
            groupEditorPagedProducts: groupEditor.pagedItems,
            onUngroupSelectedProducts: groupEditor.onUngroupSelected,
        }),
    });
};
